// src/pages/SchoolStructure/classrooms/ClassroomDetails.tsx
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Spinner from "../../../components/Spinner";
import ErrorPage from "../../../components/ErrorPage";
import Modal from "../../../components/Modal";
import ClassParentsSection from "../../../components/ClassParentsSection";
import ClassLinksSection from "../../../components/ClassLinksSection";
import { AuthContext } from "../../../AuthProvider";
import http from "../../../api/apiClient";
import { calculateAge, parseToDate } from "../../../utils/dateUtils";
import { translateClassName } from "../../../utils/classroomUtils";
import { useCan } from "../../../authz/useCan";
import { parentEmailsPermission } from "../../../authz/featurePermissionContracts";
import { mapHttpErrorToMessage } from "../../../utils/mapHttpErrorToMessage";
import {
  FaArrowRight,
  FaCalendarAlt,
  FaCalendarPlus,
  FaChalkboardTeacher,
  FaChevronDown,
  FaClipboardList,
  FaEdit,
  FaEnvelope,
  FaEllipsisV,
  FaExchangeAlt,
  FaGraduationCap,
  FaInfoCircle,
  FaMapMarkerAlt,
  FaUserGraduate,
  FaUsers,
} from "react-icons/fa";
import TeacherSection from "../../classroom-details/TeacherSection";
import ChildrenSection from "../../classroom-details/ChildrenSection";
import { OutingModals, OutingOverviewCard } from "../../classroom-details/OutingControls";
import type { ClassroomCalendarOverviewItem } from "../../classroom-details/OutingControls";
import { useOutings } from "../../classroom-details/useOutings";
import {
  Classroom,
  Child,
  FeedbackState,
  TeacherAssignment,
} from "../../classroom-details/types";
import { API_PATH as SCHOOL_CALENDAR_API_PATH } from "../../schoolCalendar/constants";
import {
  buildCalendarDateTime,
  entityId,
  eventClassroomUuids,
  eventScope,
  normalizeList,
} from "../../schoolCalendar/utils";
import type { SchoolCalendarEvent } from "../../schoolCalendar/types";
import { childDetailsPath } from "../../Families/navigation";
import { BackButton, LdsPage, StatusBadge, cardEntryClass } from "../components/Lds";
import { teacherAssignmentName } from "./teacherAssignmentUtils";


const ErrorPageComponent = ErrorPage as React.ComponentType<{
  message?: React.ReactNode;
  status?: number;
  onRetry?: () => void;
  showLogout?: boolean;
  onLogout?: () => void;
}>;

const httpStatus = (error: unknown): number | undefined =>
  typeof error === "object" &&
  error !== null &&
  "response" in error &&
  typeof (error as { response?: { status?: unknown } }).response?.status === "number"
    ? (error as { response: { status: number } }).response.status
    : undefined;

const isValidEmailAddress = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

interface ClassroomSchoolYearRecord {
  schoolYear?: ClassroomSchoolYearRecord | null;
  yearDto?: ClassroomSchoolYearRecord | null;
  schoolYearDto?: ClassroomSchoolYearRecord | null;
  academicYearObject?: ClassroomSchoolYearRecord | null;
  schoolYearUuid?: string | null;
  schoolYearId?: string | null;
  school_year_uuid?: string | null;
  school_year_id?: string | null;
  uuid?: string | null;
  id?: string | null;
}

type CalendarActivityType = "ACTIVITY" | "OUTING" | "OTHER";

interface CalendarItemFormState {
  title: string;
  description: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  allDay: boolean;
  activityType: CalendarActivityType;
  visibleInParentPortal: boolean;
}

const todayInputDate = () => new Date().toISOString().slice(0, 10);

type ClassroomDetailsTab = "details" | "students" | "schedule";

const classroomDetailsTabFromQuery = (value: string | null): ClassroomDetailsTab =>
  value === "students" || value === "schedule" ? value : "details";

const toSchoolCalendarEventType = (activityType: CalendarActivityType) => {
  if (activityType === "ACTIVITY") return "CLASS_ACTIVITY";
  if (activityType === "OUTING") return "FIELD_TRIP";
  return "OTHER";
};

const getClassroomSchoolYearUuid = (classroom?: Classroom | null) => {
  const record = classroom as ClassroomSchoolYearRecord | null;
  if (!record) return "";
  const nested =
    record.schoolYear ||
    record.yearDto ||
    record.schoolYearDto ||
    record.academicYearObject ||
    null;

  return (
    record.schoolYearUuid ||
    record.schoolYearId ||
    record.school_year_uuid ||
    record.school_year_id ||
    nested?.uuid ||
    nested?.id ||
    nested?.schoolYearUuid ||
    nested?.schoolYearId ||
    ""
  );
};

const ClassroomDetails: React.FC = () => {
  const { keycloak } = useContext(AuthContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();

  const canViewClassroomGrant = useCan("school.classrooms", "view");
  const canReadClassroomGrant = useCan("school.classrooms", "read");
  const canViewClassroom = canViewClassroomGrant || canReadClassroomGrant;
  const canManage = useCan("school.classrooms", "manage");
  const canViewChild = useCan("family.children", "read");
  const canOutingManage = useCan("outing.info", "manage");
  const canOutingRead = useCan("outing.info", "read");
  const canReadClassroomCalendar = useCan("classroom.calendar", "read");
  const canManageClassroomCalendar = useCan("classroom.calendar", "manage");
  const canManageParentMeetings = useCan("my.parent-meetings", "manage");
  const canViewParentEmails = useCan(
    parentEmailsPermission.resource,
    parentEmailsPermission.action
  );

  const [classroom, setClassroom] = useState<Classroom | null>(null);
  const [children, setChildren] = useState<Child[]>([]);
  const [teachersActive, setTeachersActive] = useState<TeacherAssignment[]>([]);
  const [teachersHistory, setTeachersHistory] = useState<TeacherAssignment[]>([]);
  const [teacherTab, setTeacherTab] = useState<"active" | "history">("active");
  const [emailCopyFeedback, setEmailCopyFeedback] = useState<FeedbackState | null>(null);
  const [parentEmails, setParentEmails] = useState<string[]>([]);
  const [parentEmailLoading, setParentEmailLoading] = useState(false);
  const [childToUnassign, setChildToUnassign] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDeleteTeacherConfirm, setShowDeleteTeacherConfirm] = useState(false);
  const [teacherToDelete, setTeacherToDelete] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ status?: number; message?: string } | null>(null);
  const [showClassActionsMenu, setShowClassActionsMenu] = useState(false);
  const classActionsMenuRef = useRef<HTMLDivElement | null>(null);
  const [activeTab, setActiveTab] = useState<ClassroomDetailsTab>(() =>
    classroomDetailsTabFromQuery(searchParams.get("tab")),
  );
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [classroomCalendarItems, setClassroomCalendarItems] = useState<ClassroomCalendarOverviewItem[]>([]);
  const [classroomCalendarLoading, setClassroomCalendarLoading] = useState(false);
  const [calendarSaving, setCalendarSaving] = useState(false);
  const [calendarError, setCalendarError] = useState<string | null>(null);
  const [calendarForm, setCalendarForm] = useState<CalendarItemFormState>(() => {
    const today = todayInputDate();
    return {
      title: "",
      description: "",
      startDate: today,
      startTime: "09:00",
      endDate: today,
      endTime: "10:00",
      allDay: false,
      activityType: "ACTIVITY",
      visibleInParentPortal: true,
    };
  });

  const outings = useOutings({
    classroomId: id || "",
    canRead: canOutingRead || canOutingManage,
    canManage: canOutingManage,
    t,
  });

  const canReadOutings = canOutingRead || canOutingManage;
  const canViewClassroomCalendarItems = canReadClassroomCalendar || canManageClassroomCalendar;

  useEffect(() => {
    setActiveTab(classroomDetailsTabFromQuery(searchParams.get("tab")));
  }, [searchParams]);

  const selectTab = (tab: ClassroomDetailsTab) => {
    setActiveTab(tab);
    const nextSearchParams = new URLSearchParams(searchParams);
    if (tab === "details") nextSearchParams.delete("tab");
    else nextSearchParams.set("tab", tab);
    setSearchParams(nextSearchParams, { replace: true, state: location.state });
  };

  useEffect(() => {
    if (!showClassActionsMenu || typeof document === "undefined") {
      return undefined;
    }

    const closeWhenOutside = (event: MouseEvent | FocusEvent) => {
      const target = event.target;
      if (target instanceof Node && classActionsMenuRef.current?.contains(target)) {
        return;
      }
      setShowClassActionsMenu(false);
    };

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShowClassActionsMenu(false);
      }
    };

    document.addEventListener("mousedown", closeWhenOutside);
    document.addEventListener("focusin", closeWhenOutside);
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("mousedown", closeWhenOutside);
      document.removeEventListener("focusin", closeWhenOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [showClassActionsMenu]);


  const loadClassroomCalendarItems = useCallback(async () => {
    if (!canViewClassroomCalendarItems || !id) {
      setClassroomCalendarItems([]);
      return;
    }

    try {
      setClassroomCalendarLoading(true);
      const schoolYearUuid = getClassroomSchoolYearUuid(classroom);
      const { data } = await http.get(SCHOOL_CALENDAR_API_PATH, {
        params: schoolYearUuid ? { schoolYearUuid } : undefined,
      });
      const events = normalizeList<SchoolCalendarEvent>(data);
      const nextItems = events
        .filter((event) => eventScope(event) === "CLASSROOM")
        .filter((event) => eventClassroomUuids(event).map(String).includes(String(id)))
        .map((event) => ({
          id: String(entityId(event) || `${event.title || "calendar"}-${event.startDate || ""}`),
          title: event.title || "",
          description: event.description || "",
          eventType: event.eventType || "",
          startDate: event.startDate || "",
          endDate: event.endDate || "",
          allDay: Boolean(event.allDay),
        }));
      setClassroomCalendarItems(nextItems);
    } catch {
      setClassroomCalendarItems([]);
    } finally {
      setClassroomCalendarLoading(false);
    }
  }, [canViewClassroomCalendarItems, classroom, id]);

  const refreshChildren = useCallback(async () => {
    try {
      const kidsRes = await http.get(`/classrooms/${id}/children`);
      const kids = Array.isArray(kidsRes?.data) ? kidsRes.data : [];
      setChildren(kids);
    } catch (err) {
      // Children fetch failure is non-blocking
    }
  }, [id]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!canViewClassroom) {
        setError({ status: 403, message: t("error.403.message") });
        setLoading(false);
        return;
      }

      let cRes;
      try {
        cRes = await http.get(`/classrooms/${id}`);
        setClassroom(cRes?.data ?? null);
      } catch (err: unknown) {
        const status = httpStatus(err);
        setError({
          status,
          message:
            status === 403
              ? t("error.403.message")
              : t("classrooms.details.load_failed", "Failed to load classroom details."),
        });
        setLoading(false);
        return;
      }

      try {
        const kidsRes = await http.get(`/classrooms/${id}/children`);
        setChildren(Array.isArray(kidsRes?.data) ? kidsRes.data : []);
      } catch {
        setChildren([]);
      }

      try {
        const activeRes = await http.get(`/teacher-classrooms/classroom/${id}`);
        setTeachersActive(Array.isArray(activeRes?.data) ? activeRes.data : []);
      } catch {
        setTeachersActive([]);
      }

      try {
        const historyRes = await http.get(`/teacher-classrooms/classroom/${id}/history`);
        const hist = Array.isArray(historyRes?.data) ? historyRes.data : [];
        setTeachersHistory(hist.filter((tch) => !!tch.endDate));
      } catch {
        setTeachersHistory([]);
      }
    } finally {
      setLoading(false);
    }
  }, [canViewClassroom, id, t]);

  useEffect(() => {
    if (keycloak && id) {
      loadAll();
    }
  }, [keycloak, id, canViewClassroom, loadAll]);

  useEffect(() => {
    if (keycloak && id && classroom) {
      loadClassroomCalendarItems();
    }
  }, [classroom, id, keycloak, loadClassroomCalendarItems]);

  const confirmUnassign = (childId: string) => {
    setChildToUnassign(childId);
    setShowConfirm(true);
  };

  const handleUnassign = async () => {
    try {
      await http.post(`/child-classrooms/unassign`, null, {
        params: { childId: childToUnassign, classroomId: id },
      });
      setChildren((prev) => prev.filter((c) => c.id !== childToUnassign));
      await refreshChildren();
    } catch (err: unknown) {
      const status = httpStatus(err);
      setError({
        message:
          status === 403 ? t("error.403.message") : t("classrooms.details.unassign_failed"),
        status,
      });
    } finally {
      setShowConfirm(false);
      setChildToUnassign(null);
    }
  };

  const confirmRemoveHistoricalAssignment = (assignmentId: string) => {
    setTeacherToDelete(assignmentId);
    setShowDeleteTeacherConfirm(true);
  };

  const handleRemoveHistoricalAssignment = async () => {
    try {
      await http.delete(`/teacher-classrooms/${teacherToDelete}`);
      setTeachersHistory((prev) => prev.filter((tch) => tch.id !== teacherToDelete));
    } catch (err: unknown) {
      const status = httpStatus(err);
      setError({
        message: status === 403 ? t("error.403.message") : t("teachers.actions.delete_failed"),
        status,
      });
    } finally {
      setShowDeleteTeacherConfirm(false);
      setTeacherToDelete(null);
    }
  };

  const makeCurrentClassroom = async (childId: string) => {
    try {
      await http.post(`/child-classrooms/set-current`, null, {
        params: { childId, classroomId: id },
      });
      await refreshChildren();
    } catch (err: unknown) {
      const status = httpStatus(err);
      setError({
        message:
          status === 403 ? t("error.403.message") : t("classrooms.details.set_current_failed"),
        status,
      });
    }
  };

  const makeCurrentClassroomSelection = async (childIds: string[]) => {
    const results = await Promise.allSettled(childIds.map((childId) =>
        http.post(`/child-classrooms/set-current`, null, {
          params: { childId, classroomId: id },
        })
      ));
    await refreshChildren();
    const failedResult = results.find(
      (result): result is PromiseRejectedResult => result.status === "rejected"
    );
    if (failedResult) {
      const err = failedResult.reason;
      const status = httpStatus(err);
      setError({
        message:
          status === 403 ? t("error.403.message") : t("classrooms.details.set_current_selection_failed"),
        status,
      });
      throw err;
    }
  };

  const fetchParentEmailsForClassroom = async (): Promise<string[]> => {
    const { data } = await http.get(`/reports/classrooms/${id}/parent-emails`);
    const input = Array.isArray(data) ? data : [];
    const emailMap = new Map<string, string>();

    input.forEach((value) => {
      if (!value || typeof value !== "string") return;
      const trimmed = value.trim();
      if (!trimmed || !isValidEmailAddress(trimmed)) return;
      const key = trimmed.toLowerCase();
      if (!emailMap.has(key)) {
        emailMap.set(key, trimmed);
      }
    });

    return Array.from(emailMap.values());
  };

  const copyTextWithFallback = async (text: string): Promise<boolean> => {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch {
        // Fall through to the legacy DOM-based copy fallback.
      }
    }

    if (typeof document === "undefined") return false;

    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.top = "-9999px";
    textarea.style.left = "-9999px";
    textarea.style.opacity = "0";

    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);

    let copied = false;
    try {
      copied = document.execCommand("copy");
    } catch {
      copied = false;
    } finally {
      document.body.removeChild(textarea);
    }

    return copied;
  };

  const copyEmailsToClipboard = async (emails: string[]) => {
    if (!emails.length) {
      setEmailCopyFeedback({
        type: "error",
        message: t("classrooms.details.parent_emails_none", {
          defaultValue: "No parent email addresses were found for this classroom.",
        }),
      });
      return;
    }

    const copied = await copyTextWithFallback(emails.join("; "));

    if (copied) {
      setEmailCopyFeedback({
        type: "success",
        message: t("classrooms.details.parent_emails_copied", {
          count: emails.length,
          defaultValue: "Email addresses copied to the clipboard.",
        }),
      });
      return;
    }

    setEmailCopyFeedback({
      type: "error",
      message: t("classrooms.details.parent_emails_copy_failed", {
        defaultValue: "Could not copy the email addresses.",
      }),
    });
  };

  const handleCopyParentEmails = async () => {
    if (parentEmailLoading) return;
    setEmailCopyFeedback(null);

    if (parentEmails.length > 0) {
      await copyEmailsToClipboard(parentEmails);
      return;
    }

    try {
      setParentEmailLoading(true);
      const emails = await fetchParentEmailsForClassroom();
      setParentEmails(emails);
      if (!emails.length) {
        setEmailCopyFeedback({
          type: "error",
          message: t("classrooms.details.parent_emails_none", {
            defaultValue: "No parent email addresses found for this classroom.",
          }),
        });
        return;
      }
      await copyEmailsToClipboard(emails);
    } catch (err) {
      const mapped = mapHttpErrorToMessage(err, t);
      setEmailCopyFeedback({
        type: "error",
        message:
          typeof mapped === "string"
            ? mapped
            : mapped?.message ||
              mapped?.title ||
              t("classrooms.details.parent_emails_copy_failed", {
                defaultValue: "Copying to the clipboard failed.",
              }),
      });
    } finally {
      setParentEmailLoading(false);
    }
  };

  const handleViewChild = (childOrIdentifier: Child | string | number) => {
    if (!canViewChild) return;

    const childIdentifier =
      typeof childOrIdentifier === "object" && childOrIdentifier !== null
        ? (
            childOrIdentifier as Child & {
              uuid?: string | number | null;
              childUuid?: string | number | null;
              childId?: string | number | null;
            }
          ).uuid ??
          (
            childOrIdentifier as Child & {
              childUuid?: string | number | null;
            }
          ).childUuid ??
          childOrIdentifier.id ??
          (
            childOrIdentifier as Child & {
              childId?: string | number | null;
            }
          ).childId
        : childOrIdentifier;

    if (
      (typeof childIdentifier !== "string" &&
        typeof childIdentifier !== "number") ||
      String(childIdentifier).trim() === ""
    ) {
      console.error(
        "Cannot open child details: missing child identifier",
        childOrIdentifier,
      );
      return;
    }

    navigate(childDetailsPath(childIdentifier), {
      state: {
        returnTo: `/school/classrooms/${id}/details?tab=students`,
      },
    });
  };

  const openCalendarModal = () => {
    const today = todayInputDate();
    setCalendarForm({
      title: "",
      description: "",
      startDate: today,
      startTime: "09:00",
      endDate: today,
      endTime: "10:00",
      allDay: false,
      activityType: "ACTIVITY",
      visibleInParentPortal: true,
    });
    setCalendarError(null);
    setShowCalendarModal(true);
    setShowClassActionsMenu(false);
  };

  const handleSubmitCalendarItem = async () => {
    const title = calendarForm.title.trim();
    if (!title) {
      setCalendarError(
        t("classroomDetails.calendarModal.validation.titleRequired", {
          defaultValue: "Title is required.",
        }),
      );
      return;
    }
    if (!calendarForm.startDate || !calendarForm.endDate) {
      setCalendarError(
        t("classroomDetails.calendarModal.validation.datesRequired", {
          defaultValue: "Start and end date are required.",
        }),
      );
      return;
    }

    const startComparable = `${calendarForm.startDate}T${
      calendarForm.allDay ? "00:00" : calendarForm.startTime || "00:00"
    }`;
    const endComparable = `${calendarForm.endDate}T${
      calendarForm.allDay ? "23:59" : calendarForm.endTime || "00:00"
    }`;

    if (new Date(endComparable).getTime() <= new Date(startComparable).getTime()) {
      setCalendarError(
        t("classroomDetails.calendarModal.validation.endAfterStart", {
          defaultValue: "End must be after start.",
        }),
      );
      return;
    }

    try {
      setCalendarSaving(true);
      setCalendarError(null);
      await http.post(SCHOOL_CALENDAR_API_PATH, {
        scope: "CLASSROOM",
        classroomUuids: id ? [id] : [],
        schoolYearUuid: getClassroomSchoolYearUuid(classroom) || null,
        title,
        description: calendarForm.description.trim(),
        eventType: toSchoolCalendarEventType(calendarForm.activityType),
        startDate: buildCalendarDateTime(
          calendarForm.startDate,
          calendarForm.startTime,
          calendarForm.allDay,
          false,
        ),
        endDate: buildCalendarDateTime(
          calendarForm.endDate,
          calendarForm.endTime,
          calendarForm.allDay,
          true,
        ),
        allDay: calendarForm.allDay,
        location: "",
        published: calendarForm.visibleInParentPortal,
      });
      setShowCalendarModal(false);
      await loadClassroomCalendarItems();
      setEmailCopyFeedback({
        type: "success",
        message: t("classroomDetails.calendarModal.saved", {
          defaultValue: "Calendar item saved.",
        }),
      });
    } catch (err: unknown) {
      const mapped = mapHttpErrorToMessage(err, t);
      setCalendarError(
        typeof mapped === "string"
          ? mapped
          : mapped?.message ||
              mapped?.title ||
              t("classroomDetails.calendarModal.saveFailed", {
                defaultValue: "Could not save the calendar item.",
              }),
      );
    } finally {
      setCalendarSaving(false);
    }
  };

  useEffect(() => {
    if (!emailCopyFeedback) return undefined;
    const timeout = setTimeout(() => setEmailCopyFeedback(null), 4000);
    return () => clearTimeout(timeout);
  }, [emailCopyFeedback]);

  useEffect(() => {
    setParentEmails([]);
    setEmailCopyFeedback(null);
  }, [id]);

  useEffect(() => {
    setParentEmails([]);
  }, [children]);

  const genderStats = useMemo(() => {
    if (!children || children.length === 0) return null;
    const counts: Record<string, number> = { male: 0, female: 0, other: 0 };
    children.forEach((child) => {
      if (child.gender === "M") counts.male += 1;
      else if (child.gender === "F") counts.female += 1;
      else counts.other += 1;
    });
    const total = counts.male + counts.female + counts.other;
    return total > 0 ? { counts, total } : null;
  }, [children]);

  const kindergartenAgeDistribution = useMemo(() => {
    if (classroom?.classType !== "KINDERGARTEN" || !children || children.length === 0) {
      return null;
    }
    const startDate = parseToDate(classroom.startDate);
    const referenceDate = startDate ? new Date(startDate.getFullYear(), 11, 31) : new Date();
    const counts = { young: 0, middle: 0, old: 0 };

    children.forEach((child) => {
      const age = calculateAge(child.dateBirth, referenceDate);
      if (!Number.isFinite(age)) return;
      if (age <= 4) counts.young += 1;
      else if (age === 5) counts.middle += 1;
      else if (age >= 6) counts.old += 1;
    });

    const total = counts.young + counts.middle + counts.old;
    return total > 0 ? { counts, total } : null;
  }, [classroom?.classType, classroom?.startDate, children]);

  const formatDate = (value?: string | null) => {
    const parsed = parseToDate(value);
    if (!parsed) return "—";
    return new Intl.DateTimeFormat("nl-NL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(parsed);
  };

  const teacherAssignmentRole = (assignment?: TeacherAssignment | null) => {
    if (!assignment) return "";
    const record = assignment as TeacherAssignment & {
      role?: string | null;
      assignmentRole?: string | null;
      teacherRole?: string | null;
    };
    return record.role || record.assignmentRole || record.teacherRole || "";
  };

  const classroomValue = (...keys: string[]) => {
    const record = classroom as unknown as Record<string, unknown>;
    for (const key of keys) {
      const value = record?.[key];
      if (typeof value === "string" && value.trim()) return value.trim();
      if (typeof value === "number") return String(value);
    }
    return "";
  };

  const primaryTeacher =
    teachersActive.find((assignment) =>
      ["PRIMARY", "CLASS_TEACHER", "MENTOR", "MAIN", "LEAD"].includes(teacherAssignmentRole(assignment).toUpperCase()),
    ) || teachersActive[0] || null;

  const primaryTeacherName =
    teacherAssignmentName(primaryTeacher) ||
    t("classrooms.details.no_teacher", { defaultValue: "No teacher assigned" });
  const classroomTeamSummary = teachersActive.length
    ? teachersActive.map((assignment) => {
        const roleCode = teacherAssignmentRole(assignment) || "PRIMARY";
        const fallbackRole = roleCode.toLowerCase().replaceAll("_", " ");
        const role = t(`classrooms.roles.${roleCode.toLowerCase()}`, {
          defaultValue: fallbackRole.charAt(0).toUpperCase() + fallbackRole.slice(1),
        });
        const subjects = assignment.subjectNames?.length ? ` · ${assignment.subjectNames.join(", ")}` : "";
        return `${teacherAssignmentName(assignment)} (${role}${subjects})`;
      }).join(" · ")
    : primaryTeacherName;

  const schoolYearStart = parseToDate(classroom?.startDate);
  const schoolYearEnd = parseToDate(classroom?.endDate);
  const schoolYearDisplay = `${schoolYearStart ? schoolYearStart.getFullYear() : "—"} – ${
    schoolYearEnd ? schoolYearEnd.getFullYear() : "—"
  }`;
  const campusDisplay =
    classroomValue("campus", "campusName", "location", "locationName", "site", "siteName") || "—";
  const descriptionDisplay =
    classroomValue("description", "classDescription", "notes") || "—";

  const agendaPreview = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const datedItems = classroomCalendarItems
      .map((item) => ({
        item,
        timestamp: parseToDate(item.startDate)?.getTime() ?? null,
      }))
      .filter(
        (
          entry,
        ): entry is {
          item: ClassroomCalendarOverviewItem;
          timestamp: number;
        } => entry.timestamp !== null,
      );

    const nextUpcoming =
      datedItems
        .filter((entry) => entry.timestamp >= today.getTime())
        .sort((left, right) => left.timestamp - right.timestamp)[0] ?? null;

    if (nextUpcoming) {
      return {
        item: nextUpcoming.item,
        isUpcoming: true,
      };
    }

    const latestExisting =
      datedItems.sort((left, right) => right.timestamp - left.timestamp)[0] ?? null;

    return latestExisting
      ? {
          item: latestExisting.item,
          isUpcoming: false,
        }
      : null;
  }, [classroomCalendarItems]);

  const nextAgendaItem = agendaPreview?.item ?? null;

  const childPreviewName = (child: Child) => {
    const record = child as Child & {
      firstName?: string | null;
      lastName?: string | null;
      fullName?: string | null;
      name?: string | null;
    };

    return (
      record.fullName ||
      record.name ||
      [record.firstName, record.lastName].filter(Boolean).join(" ").trim() ||
      "—"
    );
  };

  const childPreviewId = (child: Child, index: number) => {
    const record = child as Child & {
      id?: string | number | null;
      uuid?: string | number | null;
      childId?: string | number | null;
    };

    return String(record.id ?? record.uuid ?? record.childId ?? index);
  };

  const previewChildren = children.slice(0, 5);

  const summaryCards = useMemo(() => {
    if (!classroom) return [];
    const start = parseToDate(classroom.startDate);
    const end = parseToDate(classroom.endDate);
    return [
      {
        label: t("classrooms.table.name"),
        value: translateClassName(classroom),
        helper:
          classroom.classType === "KINDERGARTEN"
            ? t("classrooms.kindergarten.label")
            : t("classrooms.grade.label"),
        tone: "indigo" as const,
      },
      {
        label: t("classrooms.details.capacity"),
        value: `${children.length} / ${classroom.maxChildren ?? "—"}`,
        helper: t("classrooms.details.children_assigned", { defaultValue: "Children assigned" }),
        tone: "emerald" as const,
      },
      {
        label: t("years.common.name"),
        value: `${start ? start.getFullYear() : "—"} - ${end ? end.getFullYear() : "—"}`,
        helper: t("classrooms.details.school_year"),
        tone: "amber" as const,
      },
    ];
  }, [classroom, children.length, t]);

  const ageColors = useMemo(
    () => ({
      young: "bg-emerald-500",
      middle: "bg-amber-500",
      old: "bg-purple-600",
    }),
    []
  );

  const canShowClassActions =
    canManage ||
    canReadOutings ||
    canManageClassroomCalendar ||
    canManageParentMeetings;
  const refreshAgendaActivities = useCallback(() => {
    if (canReadOutings) {
      outings.fetchOutingOverview();
    }
    loadClassroomCalendarItems();
  }, [canReadOutings, loadClassroomCalendarItems, outings]);

  if (loading) return <Spinner />;
  if (error)
    return (
      <ErrorPageComponent
        message={error.message}
        status={error.status}
        onRetry={() => window.location.reload()}
      />
    );
  if (!classroom)
    return <p className="text-gray-600 dark:text-gray-300">{t("classrooms.common.none_available")}</p>;

  const classActionsMenu = canShowClassActions ? (
    <div className="w-80 rounded-[28px] border border-indigo-100/70 bg-white/95 p-3 text-left shadow-[0_25px_70px_rgba(15,23,42,0.18)] backdrop-blur dark:border-indigo-700/60 dark:bg-gray-900/95">
      <div className="space-y-3">
        {(canManageClassroomCalendar || canReadOutings) ? (
          <div>
            <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {t("classroomDetails.actions.agendaActivities", {
                defaultValue: "Agenda & activities",
              })}
            </p>

            {canManageClassroomCalendar ? (
              <button
                type="button"
                onClick={openCalendarModal}
                className="flex w-full items-start gap-3 rounded-2xl px-3 py-2.5 text-left text-sm text-gray-800 transition hover:bg-indigo-50 dark:text-gray-100 dark:hover:bg-gray-800/70"
              >
                <span className="mt-0.5 text-indigo-600 dark:text-indigo-300">
                  <FaCalendarPlus aria-hidden="true" />
                </span>
                <span>
                  <span className="block font-semibold">
                    {t("classroomDetails.actions.newCalendarItem", {
                      defaultValue: "New calendar item",
                    })}
                  </span>
                  <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                    {t("classroomDetails.calendarModal.activityHint", {
                      defaultValue:
                        "Use a calendar item for activities that do not require parent consent, such as a forest day.",
                    })}
                  </span>
                </span>
              </button>
            ) : null}

            {canOutingManage ? (
              <button
                type="button"
                onClick={() => {
                  outings.setOutingModalError(null);
                  outings.setShowOutingModal(true);
                  setShowClassActionsMenu(false);
                }}
                className="flex w-full items-start gap-3 rounded-2xl px-3 py-2.5 text-left text-sm text-gray-800 transition hover:bg-emerald-50 dark:text-gray-100 dark:hover:bg-gray-800/70"
              >
                <span className="mt-0.5 text-emerald-600 dark:text-emerald-300">
                  <FaClipboardList aria-hidden="true" />
                </span>
                <span>
                  <span className="block font-semibold">
                    {t("classroomDetails.actions.newOuting", {
                      defaultValue: "New outing",
                    })}
                  </span>
                  <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                    {t("classroomDetails.outingHint", {
                      defaultValue:
                        "Use an outing when parent consent or forms are required.",
                    })}
                  </span>
                </span>
              </button>
            ) : null}

            {canReadOutings ? (
              <button
                type="button"
                onClick={() => {
                  outings.fetchOutingOverview();
                  outings.setShowStatusPopover(false);
                  outings.setShowOutingOverviewModal(true);
                  setShowClassActionsMenu(false);
                }}
                className="flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left text-sm font-semibold text-gray-800 transition hover:bg-sky-50 dark:text-gray-100 dark:hover:bg-gray-800/70"
              >
                <span className="text-sky-600 dark:text-sky-300">
                  <FaMapMarkerAlt aria-hidden="true" />
                </span>
                {t("classroomDetails.actions.outingsForms", {
                  defaultValue: "Outings & forms",
                })}
              </button>
            ) : null}
          </div>
        ) : null}

        {canManageParentMeetings ? (
          <div className="border-t border-gray-100 pt-3 dark:border-gray-800">
            <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {t("classroomDetails.actions.planning", {
                defaultValue: "Planning",
              })}
            </p>
            <button
              type="button"
              onClick={() => {
                setShowClassActionsMenu(false);
                navigate(`/parent-meetings/${id}/new`);
              }}
              className="flex w-full items-start gap-3 rounded-2xl px-3 py-2.5 text-left text-sm text-gray-800 transition hover:bg-purple-50 dark:text-gray-100 dark:hover:bg-gray-800/70"
            >
              <span className="mt-0.5 text-purple-600 dark:text-purple-300">
                <FaUsers aria-hidden="true" />
              </span>
              <span>
                <span className="block font-semibold">
                  {t("classroomDetails.actions.planParentMeetings", {
                    defaultValue: "Plan parent meetings",
                  })}
                </span>
                <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                  {t("classroomDetails.actions.parentMeetingsHint", {
                    defaultValue:
                      "Specific planning where parents choose a meeting time slot.",
                  })}
                </span>
              </span>
            </button>
          </div>
        ) : null}

        {canManage ? (
          <div className="border-t border-gray-100 pt-3 dark:border-gray-800">
            <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {t("classroomDetails.actions.management", {
                defaultValue: "Management",
              })}
            </p>
            <button
              type="button"
              onClick={() => {
                setShowClassActionsMenu(false);
                navigate(`/school/classrooms/${id}/migrate`);
              }}
              className="flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left text-sm font-semibold text-gray-800 transition hover:bg-amber-50 dark:text-gray-100 dark:hover:bg-gray-800/70"
            >
              <span className="text-amber-600 dark:text-amber-300">
                <FaExchangeAlt aria-hidden="true" />
              </span>
              {t("classrooms.details.migrate", {
                defaultValue: "Migrate classroom",
              })}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  ) : null;

  const detailTabs = [
    {
      id: "details" as const,
      label: t("classrooms.details.tabs.details", { defaultValue: "Details" }),
      icon: FaInfoCircle,
    },
    {
      id: "students" as const,
      label: t("classrooms.details.tabs.students", { defaultValue: "Students" }),
      icon: FaUserGraduate,
    },
    {
      id: "schedule" as const,
      label: t("classrooms.details.tabs.schedule", { defaultValue: "Schedule" }),
      icon: FaCalendarAlt,
    },
  ];

  const detailRows = [
    {
      label: t("classrooms.overview.table.name", {
        defaultValue: "Classroom name",
      }),
      value: translateClassName(classroom),
    },
    {
      label: t("classrooms.filters.school_year", {
        defaultValue: "School year",
      }),
      value: schoolYearDisplay,
    },
    {
      label: t("classrooms.details.start_date", {
        defaultValue: "Start date",
      }),
      value: formatDate(classroom.startDate),
    },
    {
      label: t("classrooms.details.end_date", {
        defaultValue: "End date",
      }),
      value: formatDate(classroom.endDate),
    }
  ];

  return (
    <LdsPage maxWidth="max-w-7xl">
      <>
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-[24px] border border-slate-200/80 bg-white/95 px-5 py-4 shadow-sm dark:border-white/10 dark:bg-slate-950/80 sm:px-7">
            <BackButton fallback="/school/classrooms" />

            <div className="flex items-center gap-2">
              {canManage ? (
                <button
                  type="button"
                  onClick={() => navigate(`/school/classrooms/${id}/edit`)}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 shadow-sm transition hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:border-white/10 dark:bg-white/5 dark:text-slate-100 dark:hover:bg-indigo-400/10 dark:hover:text-indigo-200"
                >
                  <FaEdit aria-hidden="true" />
                  {t("common.actions.edit", { defaultValue: "Edit" })}
                </button>
              ) : null}

              {canShowClassActions ? (
                <div ref={classActionsMenuRef} className="relative">
                  <button
                    type="button"
                    onClick={() => setShowClassActionsMenu((current) => !current)}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-indigo-400/10"
                    aria-label={t("classroomDetails.actions.title", {
                      defaultValue: "Class actions",
                    })}
                    aria-expanded={showClassActionsMenu}
                  >
                    <FaEllipsisV aria-hidden="true" />
                  </button>
                  {showClassActionsMenu ? (
                    <div className="absolute right-0 z-40 mt-3 w-80">
                      {classActionsMenu}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>

          <div className="relative overflow-hidden rounded-[30px] border border-slate-200/80 bg-gradient-to-r from-white via-white to-sky-50/90 px-5 py-6 shadow-[0_22px_65px_rgba(15,23,42,0.08)] dark:border-white/10 dark:from-slate-950 dark:via-slate-950 dark:to-sky-950/25 sm:px-7 sm:py-7">
            <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-[42%] lg:block" aria-hidden="true">
              <div className="absolute bottom-0 right-0 h-28 w-full rounded-tl-[90px] bg-sky-100/70 dark:bg-sky-300/10" />
              <FaChalkboardTeacher className="absolute bottom-3 right-10 text-[132px] text-sky-200/80 dark:text-sky-700/25" />
              <FaGraduationCap className="absolute bottom-12 right-56 text-6xl text-indigo-200/80 dark:text-indigo-700/30" />
            </div>

            <div className="relative z-10 flex min-w-0 items-center gap-5 pr-0 lg:pr-[35%]">
              <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-sky-100 to-indigo-100 text-4xl text-sky-600 shadow-[0_12px_35px_rgba(37,99,235,0.18)] ring-1 ring-sky-100 dark:border-slate-950 dark:from-sky-950 dark:to-indigo-950 dark:text-sky-300 dark:ring-sky-400/20">
                <FaGraduationCap aria-hidden="true" />
              </div>

              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-indigo-600 dark:text-indigo-300">
                  {t("classrooms.details.eyebrow", { defaultValue: "Classroom details" })}
                </p>
                <h1 className="mt-1 truncate text-3xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-4xl">
                  {translateClassName(classroom)}
                </h1>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                  <StatusBadge tone="success">
                    {t("common.active", { defaultValue: "Active" })}
                  </StatusBadge>
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 dark:text-slate-300">
                    <FaChalkboardTeacher
                      aria-hidden="true"
                      className="text-indigo-500 dark:text-indigo-300"
                    />
                    {t("classrooms.details.team", { defaultValue: "Class team" })}:{" "}
                    <strong className="text-slate-900 dark:text-white">
                      {classroomTeamSummary}
                    </strong>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {classroom.classType === "GRADE" &&
          classroom.grade &&
          !String(classroom.className || "")
            .toLowerCase()
            .includes(String(classroom.grade)) ? (
            <div className="mx-5 mt-5 rounded-2xl border border-amber-200/70 bg-amber-50/80 p-4 text-sm text-amber-900 shadow-sm dark:border-amber-500/40 dark:bg-amber-900/30 dark:text-amber-100 sm:mx-7">
              {t("classrooms.details.grade_mismatch", {
                name: classroom.className,
                number: classroom.grade,
              })}
            </div>
          ) : null}

          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            {[
              {
                icon: FaCalendarAlt,
                label: t("classrooms.filters.school_year", {
                  defaultValue: "School year",
                }),
                value: schoolYearDisplay,
                helper: t("classrooms.details.current_school_year", {
                  defaultValue: "Current school year",
                }),
              },
              {
                icon: FaUserGraduate,
                label: t("classrooms.details.students", {
                  defaultValue: "Students",
                }),
                value: String(children.length),
                helper: t("classrooms.details.linked", {
                  defaultValue: "Linked",
                }),
              },
              {
                icon: FaCalendarPlus,
                label: agendaPreview?.isUpcoming
                  ? t("classrooms.details.next_agenda_item", {
                      defaultValue: "Next agenda item",
                    })
                  : t("classrooms.details.latest_agenda_item", {
                      defaultValue: "Latest agenda item",
                    }),
                value:
                  nextAgendaItem?.title ||
                  t("classrooms.details.no_upcoming_items", {
                    defaultValue: "No upcoming items",
                  }),
                helper: nextAgendaItem
                  ? formatDate(nextAgendaItem.startDate)
                  : t("classrooms.details.schedule_empty", {
                      defaultValue: "Nothing planned yet",
                    }),
              },
            ].map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={String(card.label)}
                  className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-white/5"
                >
                  <p className="inline-flex items-center gap-2 text-xs font-bold text-indigo-600 dark:text-indigo-300">
                    <Icon aria-hidden="true" />
                    {card.label}
                  </p>
                  <p className="mt-2 truncate text-base font-bold text-slate-950 dark:text-white">
                    {card.value}
                  </p>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    {card.helper}
                  </p>
                </div>
              );
            })}
          </div>

          <div
            className="mt-5 grid overflow-hidden rounded-[24px] border border-slate-200/80 bg-white/95 shadow-sm dark:border-white/10 dark:bg-slate-950/80 sm:grid-cols-3"
            role="tablist"
            aria-label={t("classrooms.details.tabs.label", {
              defaultValue: "Classroom sections",
            })}
          >
            {detailTabs.map((tab) => {
              const Icon = tab.icon;
              const selected = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => selectTab(tab.id)}
                  className={`relative inline-flex min-h-[58px] items-center justify-center gap-2 px-4 text-sm font-bold transition ${
                    selected
                      ? "text-sky-600 dark:text-sky-300"
                      : "text-slate-600 hover:bg-slate-50 hover:text-indigo-700 dark:text-slate-300 dark:hover:bg-white/5 dark:hover:text-indigo-200"
                  }`}
                >
                  <Icon aria-hidden="true" />
                  {tab.label}
                  {selected ? (
                    <span className="absolute inset-x-0 bottom-0 h-1 rounded-t-full bg-sky-500" />
                  ) : null}
                </button>
              );
            })}
          </div>

          <div className="py-5 sm:py-6">
            {activeTab === "details" ? (
              <div className="grid gap-5 lg:grid-cols-[0.95fr_1.05fr]">
                <section className="rounded-2xl border border-slate-200/80 bg-white p-5 dark:border-white/10 dark:bg-white/5">
                  <h2 className="inline-flex items-center gap-2 text-base font-bold text-slate-950 dark:text-white">
                    <FaInfoCircle aria-hidden="true" className="text-sky-500" />
                    {t("classrooms.details.classroom_information", {
                      defaultValue: "Classroom information",
                    })}
                  </h2>

                  <div className="mt-4 divide-y divide-slate-200/80 dark:divide-white/10">
                    {detailRows.map((row) => (
                      <div
                        key={String(row.label)}
                        className="grid gap-1 py-3 sm:grid-cols-[145px_minmax(0,1fr)] sm:gap-4"
                      >
                        <span className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                          {row.label}
                        </span>
                        <span className="text-sm font-bold text-slate-950 dark:text-white">
                          {row.value}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-5 border-t border-slate-200/80 pt-5 dark:border-white/10">
                    <div className="flex items-start gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-indigo-600 dark:bg-indigo-400/10 dark:text-indigo-200">
                        <FaCalendarAlt aria-hidden="true" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-bold text-slate-950 dark:text-white">
                          {agendaPreview?.isUpcoming
                            ? t("classrooms.details.next_agenda_item", {
                                defaultValue: "Next agenda item",
                              })
                            : t("classrooms.details.latest_agenda_item", {
                                defaultValue: "Latest agenda item",
                              })}
                        </h3>

                        {classroomCalendarLoading ? (
                          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                            {t("common.loading", { defaultValue: "Loading..." })}
                          </p>
                        ) : nextAgendaItem ? (
                          <>
                            <p className="mt-2 truncate text-sm font-bold text-slate-950 dark:text-white">
                              {nextAgendaItem.title || "—"}
                            </p>
                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                              {formatDate(nextAgendaItem.startDate)}
                            </p>
                            {nextAgendaItem.description ? (
                              <p className="mt-2 line-clamp-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
                                {nextAgendaItem.description}
                              </p>
                            ) : null}
                          </>
                        ) : (
                          <>
                            <p className="mt-2 text-sm font-bold text-slate-950 dark:text-white">
                              {t("classrooms.details.no_upcoming_items", {
                                defaultValue: "No upcoming items",
                              })}
                            </p>
                            <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                              {t("classrooms.details.no_upcoming_items_description", {
                                defaultValue:
                                  "There are no upcoming agenda items for this classroom.",
                              })}
                            </p>
                          </>
                        )}

                        <button
                          type="button"
                          onClick={() => selectTab("schedule")}
                          className="mt-4 inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-4 py-2 text-xs font-bold text-sky-700 transition hover:border-sky-300 hover:bg-sky-100 dark:border-sky-400/25 dark:bg-sky-400/10 dark:text-sky-200"
                        >
                          {t("classrooms.details.view_schedule", {
                            defaultValue: "View schedule",
                          })}
                          <FaArrowRight aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="rounded-2xl border border-slate-200/80 bg-white p-5 dark:border-white/10 dark:bg-white/5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h2 className="inline-flex items-center gap-2 text-base font-bold text-slate-950 dark:text-white">
                      <FaUserGraduate aria-hidden="true" className="text-indigo-500" />
                      {t("classrooms.details.students", {
                        defaultValue: "Students",
                      })}
                      <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-indigo-50 px-2 text-xs font-bold text-indigo-700 dark:bg-indigo-400/10 dark:text-indigo-200">
                        {children.length}
                      </span>
                    </h2>

                    <div className="flex flex-wrap items-center gap-3">
                      {canViewParentEmails ? (
                        <button
                          type="button"
                          onClick={handleCopyParentEmails}
                          disabled={parentEmailLoading}
                          className={`lumen-glow-sheen inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-semibold transition ${
                            parentEmailLoading
                              ? "cursor-wait border-gray-200 bg-gray-100 text-gray-400 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-500"
                              : "border-emerald-200/70 bg-emerald-50/80 text-emerald-700 hover:text-emerald-900 dark:border-emerald-500/40 dark:bg-emerald-900/40 dark:text-emerald-200"
                          }`}
                        >
                          <FaEnvelope aria-hidden="true" />
                          <span>
                            {parentEmailLoading
                              ? t("classrooms.details.parent_emails_loading", {
                                  defaultValue: "Loading email addresses...",
                                })
                              : t("classrooms.details.copy_parent_emails", {
                                  count: parentEmails.length,
                                  defaultValue: "Copy parent emails",
                                })}
                            {!parentEmailLoading && parentEmails.length > 0 ? ` (${parentEmails.length})` : ""}
                          </span>
                        </button>
                      ) : null}

                      <button
                        type="button"
                        onClick={() => selectTab("students")}
                        className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-4 py-2 text-xs font-bold text-sky-700 transition hover:border-sky-300 hover:bg-sky-100 dark:border-sky-400/25 dark:bg-sky-400/10 dark:text-sky-200"
                      >
                        {t("classrooms.details.view_all_students", {
                          defaultValue: "View all students",
                        })}
                        <FaArrowRight aria-hidden="true" />
                      </button>
                    </div>
                  </div>

                  {emailCopyFeedback ? (
                    <p
                      className={`mt-2 text-xs ${
                        emailCopyFeedback.type === "success"
                          ? "text-emerald-600 dark:text-emerald-300"
                          : "text-amber-600 dark:text-amber-300"
                      }`}
                    >
                      {emailCopyFeedback.message}
                    </p>
                  ) : null}

                  {previewChildren.length > 0 ? (
                    <div className="mt-5 divide-y divide-slate-200/80 dark:divide-white/10">
                      {previewChildren.map((child, index) => {
                        const name = childPreviewName(child);
                        const age = child.dateBirth
                          ? calculateAge(child.dateBirth)
                          : null;

                        return (
                          <button
                            key={childPreviewId(child, index)}
                            type="button"
                            disabled={!canViewChild}
                            onClick={() => {
                              if (canViewChild) handleViewChild(child);
                            }}
                            className="flex w-full items-center justify-between gap-4 py-3 text-left transition enabled:hover:bg-slate-50/80 disabled:cursor-default dark:enabled:hover:bg-white/[0.035]"
                          >
                            <span className="flex min-w-0 items-center gap-3">
                              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-sm font-bold text-indigo-700 dark:bg-indigo-400/10 dark:text-indigo-200">
                                {name.charAt(0).toUpperCase() || "?"}
                              </span>
                              <span className="min-w-0">
                                <span className="block truncate text-sm font-bold text-slate-950 dark:text-white">
                                  {name}
                                </span>
                                <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">
                                  {age !== null
                                    ? t("classrooms.details.student_age", {
                                        defaultValue: "{{count}} years",
                                        count: age,
                                      })
                                    : t("classrooms.details.age_unknown", {
                                        defaultValue: "Age unknown",
                                      })}
                                </span>
                              </span>
                            </span>

                            <StatusBadge tone="success">
                              {t("classrooms.details.current", {
                                defaultValue: "Current",
                              })}
                            </StatusBadge>
                          </button>
                        );
                      })}

                      {children.length > previewChildren.length ? (
                        <p className="pt-4 text-xs text-slate-500 dark:text-slate-400">
                          {t("classrooms.details.more_students", {
                            defaultValue: "And {{count}} more students",
                            count: children.length - previewChildren.length,
                          })}
                        </p>
                      ) : null}
                    </div>
                  ) : (
                    <div className="flex min-h-[220px] flex-col items-center justify-center text-center">
                      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-xl text-slate-400 dark:bg-white/5 dark:text-slate-500">
                        <FaUserGraduate aria-hidden="true" />
                      </span>
                      <h3 className="mt-4 text-sm font-bold text-slate-950 dark:text-white">
                        {t("classrooms.details.no_students", {
                          defaultValue: "No students linked",
                        })}
                      </h3>
                      <p className="mt-1 max-w-sm text-xs leading-5 text-slate-500 dark:text-slate-400">
                        {t("classrooms.details.no_students_description", {
                          defaultValue:
                            "Students linked to this classroom will appear here.",
                        })}
                      </p>
                    </div>
                  )}
                </section>

                <div className="lg:col-span-2">
                  <div className="grid gap-5 lg:grid-cols-2">
                    <ClassParentsSection classroomId={id} />
                    <ClassLinksSection classroomId={id} />
                    <ClassLinksSection schoolWide allowManagement={false} />
                  </div>
                </div>
              </div>
            ) : null}

            {activeTab === "students" ? (
              <section className="overflow-hidden rounded-[24px] border border-slate-200/80 bg-white/95 shadow-sm dark:border-white/10 dark:bg-slate-950/75">
                <div className="flex flex-col gap-3 border-b border-slate-200/80 px-4 py-3.5 dark:border-white/10 sm:flex-row sm:items-center sm:justify-between sm:px-5">
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-400/10 dark:text-indigo-200">
                      <FaUserGraduate aria-hidden="true" />
                    </span>
                    <div className="min-w-0">
                      <h2 className="truncate text-sm font-bold text-slate-950 dark:text-white">
                        {t("classrooms.details.linked_students", {
                          defaultValue: "Linked students",
                        })}
                      </h2>
                      <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
                        {t("classrooms.details.linked_students_description", {
                          defaultValue:
                            "Manage students and view the classroom distribution.",
                        })}
                      </p>
                    </div>
                  </div>

                  <span className="inline-flex w-fit items-center rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-bold text-indigo-700 dark:border-indigo-400/25 dark:bg-indigo-400/10 dark:text-indigo-200">
                    {t("classrooms.details.student_count", {
                      defaultValue: "{{count}} students",
                      count: children.length,
                    })}
                  </span>
                </div>

                <div className="space-y-4 p-4 sm:p-5">
                  {(kindergartenAgeDistribution || genderStats) ? (
                    <div className="grid gap-3 lg:grid-cols-2">
                      {kindergartenAgeDistribution ? (
                        <section className="rounded-2xl border border-slate-200/80 bg-slate-50/60 p-4 dark:border-white/10 dark:bg-white/[0.035]">
                          <div className="flex items-center justify-between gap-3">
                            <h3 className="text-xs font-bold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                              {t("classrooms.details.age_mix", {
                                defaultValue: "Age mix",
                              })}
                            </h3>
                            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                              {kindergartenAgeDistribution.total}
                            </span>
                          </div>

                          <div className="mt-3 space-y-2.5">
                            {[
                              {
                                key: "young" as const,
                                label: t("classrooms.details.age_young", {
                                  defaultValue: "Youngest kindergarten (4 years)",
                                }),
                              },
                              {
                                key: "middle" as const,
                                label: t("classrooms.details.age_middle", {
                                  defaultValue: "Middle kindergarten (5 years)",
                                }),
                              },
                              {
                                key: "old" as const,
                                label: t("classrooms.details.age_old", {
                                  defaultValue: "Oldest kindergarten (6–7 years)",
                                }),
                              },
                            ].map((item) => {
                              const count =
                                kindergartenAgeDistribution.counts[item.key];
                              const percentage =
                                kindergartenAgeDistribution.total > 0
                                  ? Math.round(
                                      (count /
                                        kindergartenAgeDistribution.total) *
                                        100,
                                    )
                                  : 0;

                              return (
                                <div key={item.key}>
                                  <div className="flex items-center justify-between gap-3 text-xs text-slate-600 dark:text-slate-300">
                                    <span className="truncate">{item.label}</span>
                                    <span className="shrink-0 font-semibold">
                                      {count} ({percentage}%)
                                    </span>
                                  </div>
                                  <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                                    <div
                                      className={`h-full rounded-full transition-all ${
                                        ageColors[item.key]
                                      }`}
                                      style={{ width: `${percentage}%` }}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </section>
                      ) : null}

                      {genderStats ? (
                        <section className="rounded-2xl border border-slate-200/80 bg-slate-50/60 p-4 dark:border-white/10 dark:bg-white/[0.035]">
                          <div className="flex items-center justify-between gap-3">
                            <h3 className="text-xs font-bold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                              {t("classrooms.details.gender_distribution", {
                                defaultValue: "Gender distribution",
                              })}
                            </h3>
                            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                              {genderStats.total}
                            </span>
                          </div>

                          <div className="mt-3 space-y-2.5">
                            {[
                              {
                                key: "male" as const,
                                label: t("common.gender.male", {
                                  defaultValue: "Boys",
                                }),
                                barClass: "bg-sky-400",
                              },
                              {
                                key: "female" as const,
                                label: t("common.gender.female", {
                                  defaultValue: "Girls",
                                }),
                                barClass: "bg-pink-400",
                              },
                              {
                                key: "other" as const,
                                label: t("common.gender.other", {
                                  defaultValue: "Other",
                                }),
                                barClass: "bg-violet-400",
                              },
                            ].map((item) => {
                              const count = genderStats.counts[item.key];
                              const percentage =
                                genderStats.total > 0
                                  ? Math.round(
                                      (count / genderStats.total) * 100,
                                    )
                                  : 0;

                              return (
                                <div key={item.key}>
                                  <div className="flex items-center justify-between gap-3 text-xs text-slate-600 dark:text-slate-300">
                                    <span>{item.label}</span>
                                    <span className="shrink-0 font-semibold">
                                      {count} ({percentage}%)
                                    </span>
                                  </div>
                                  <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                                    <div
                                      className={`h-full rounded-full transition-all ${item.barClass}`}
                                      style={{ width: `${percentage}%` }}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </section>
                      ) : null}
                    </div>
                  ) : null}

                  <div className="[&>section]:border-0 [&>section]:bg-transparent [&>section]:p-0 [&>section]:shadow-none [&>div]:border-0 [&>div]:bg-transparent [&>div]:p-0 [&>div]:shadow-none">
                    <ChildrenSection
                      childrenList={children}
                      classroom={classroom}
                      canManage={canManage}
                      canViewChild={canViewChild}
                      canViewParentEmails={canViewParentEmails}
                      onViewChild={handleViewChild}
                      onMakeCurrent={makeCurrentClassroom}
                      onMakeCurrentSelection={makeCurrentClassroomSelection}
                      onUnassign={confirmUnassign}
                      emailCopyFeedback={emailCopyFeedback}
                      tripActionFeedback={outings.tripActionFeedback}
                      parentEmailLoading={parentEmailLoading}
                      parentEmailCount={parentEmails.length}
                      onCopyParentEmails={handleCopyParentEmails}
                      kindergartenAgeDistribution={null}
                      genderStats={null}
                      ageColors={ageColors}
                      outingOverviewCard={null}
                      outingActions={null}
                      t={t}
                    />
                  </div>
                </div>
              </section>
            ) : null}

            {activeTab === "schedule" ? (
              <section className="rounded-2xl border border-slate-200/80 bg-white p-5 dark:border-white/10 dark:bg-white/5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h2 className="inline-flex items-center gap-2 text-base font-bold text-slate-950 dark:text-white">
                      <FaCalendarAlt aria-hidden="true" className="text-sky-500" />
                      {t("classroomDetails.agendaActivities.title", {
                        defaultValue: "Schedule & activities",
                      })}
                    </h2>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      {t("classroomDetails.agendaActivities.hint", {
                        defaultValue:
                          "Classroom calendar items and outings with consent forms in one overview.",
                      })}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {(outings.outingOverviewLoading || classroomCalendarLoading) ? (
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        {t("common.loading", { defaultValue: "Loading..." })}
                      </span>
                    ) : null}
                    <button
                      type="button"
                      onClick={refreshAgendaActivities}
                      className="rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-bold text-indigo-700 transition hover:bg-indigo-100 dark:border-indigo-400/25 dark:bg-indigo-400/10 dark:text-indigo-200"
                    >
                      {t("classrooms.details.outing_overview_refresh", {
                        defaultValue: "Refresh",
                      })}
                    </button>
                  </div>
                </div>

                {canViewClassroomCalendarItems || canReadOutings ? (
                  <div className="mt-5">
                    <OutingOverviewCard
                      outings={outings}
                      t={t}
                      calendarItems={classroomCalendarItems}
                      calendarLoading={classroomCalendarLoading}
                    />
                  </div>
                ) : (
                  <p className="mt-5 text-sm text-slate-500 dark:text-slate-400">
                    {t("classrooms.details.schedule_unavailable", {
                      defaultValue: "Schedule information is not available.",
                    })}
                  </p>
                )}
              </section>
            ) : null}


            <div className="mt-5 rounded-2xl border border-sky-100 bg-gradient-to-r from-sky-50 to-indigo-50 px-5 py-4 dark:border-sky-400/20 dark:from-sky-950/30 dark:to-indigo-950/30">
              <p className="inline-flex items-center gap-2 text-sm font-bold text-sky-700 dark:text-sky-200">
                <FaClipboardList aria-hidden="true" />
                {t("classrooms.details.next_step", { defaultValue: "Next step" })}
              </p>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                {t("classrooms.details.next_step_description", {
                  defaultValue:
                    "Manage student assignments, teachers and classroom planning.",
                })}
              </p>
            </div>
          </div>
      </>

      <Modal
        show={showConfirm}
        title={t("common.modal.are_you_sure")}
        message={t("classrooms.details.confirm_unassign")}
        isConfirm
        type="confirm"
        onConfirm={handleUnassign}
        onClose={() => setShowConfirm(false)}
      />

      <Modal
        show={showDeleteTeacherConfirm}
        type="confirm"
        isConfirm
        title={t("common.modal.are_you_sure")}
        message={t("teachers.actions.confirm_delete_assignment")}
        onConfirm={handleRemoveHistoricalAssignment}
        onClose={() => {
          setShowDeleteTeacherConfirm(false);
          setTeacherToDelete(null);
        }}
      />

      <Modal
        show={showCalendarModal}
        title={t("classroomDetails.calendarModal.title", {
          defaultValue: "New calendar item",
        })}
        message={null}
        hideDefaultActions
        onClose={() => setShowCalendarModal(false)}
        footer={
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
            <button
              type="button"
              onClick={() => setShowCalendarModal(false)}
              className="rounded-full border border-gray-200/70 bg-white/80 px-4 py-2 text-sm font-semibold text-gray-700 hover:text-gray-900 dark:border-gray-700/60 dark:bg-gray-800/70 dark:text-gray-200"
            >
              {t("common.actions.cancel", { defaultValue: "Cancel" })}
            </button>
            <button
              type="button"
              onClick={handleSubmitCalendarItem}
              disabled={calendarSaving}
              className="lumen-glow-sheen inline-flex items-center justify-center rounded-full border border-indigo-300/80 bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-500 disabled:cursor-wait disabled:opacity-75 dark:border-indigo-800/60 dark:bg-indigo-700 dark:hover:bg-indigo-600"
            >
              {calendarSaving
                ? t("common.saving", { defaultValue: "Saving..." })
                : t("common.actions.save", { defaultValue: "Save" })}
            </button>
          </div>
        }
      >
        <div className="mb-3 space-y-4 text-left">
          <p className="rounded-2xl border border-indigo-100/70 bg-indigo-50/70 px-4 py-3 text-sm leading-relaxed text-gray-700 dark:border-indigo-800/50 dark:bg-indigo-950/30 dark:text-gray-200">
            {t("classroomDetails.calendarModal.activityHint", {
              defaultValue:
                "Use a calendar item for activities that do not require parent consent, such as a forest day.",
            })}
          </p>

          <label className="block text-sm font-semibold text-gray-800 dark:text-gray-100">
            {t("classroomDetails.calendarModal.name", { defaultValue: "Title" })}
            <input
              type="text"
              value={calendarForm.title}
              onChange={(event) =>
                setCalendarForm((current) => ({ ...current, title: event.target.value }))
              }
              className="lumen-glow-input mt-2 w-full rounded-2xl border border-gray-200/80 bg-white/90 px-4 py-2.5 text-sm text-gray-900 shadow-sm focus:border-indigo-400 focus:ring-indigo-400 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
            />
          </label>

          <label className="block text-sm font-semibold text-gray-800 dark:text-gray-100">
            {t("classroomDetails.calendarModal.description", { defaultValue: "Description" })}
            <textarea
              value={calendarForm.description}
              onChange={(event) =>
                setCalendarForm((current) => ({ ...current, description: event.target.value }))
              }
              rows={3}
              className="lumen-glow-input mt-2 w-full rounded-2xl border border-gray-200/80 bg-white/90 px-4 py-2.5 text-sm text-gray-900 shadow-sm focus:border-indigo-400 focus:ring-indigo-400 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
            />
          </label>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm font-semibold text-gray-800 dark:text-gray-100">
              {t("classroomDetails.calendarModal.type", { defaultValue: "Activity type" })}
              <select
                value={calendarForm.activityType}
                onChange={(event) =>
                  setCalendarForm((current) => ({
                    ...current,
                    activityType: event.target.value as CalendarActivityType,
                  }))
                }
                className="mt-2 w-full rounded-2xl border border-gray-200/80 bg-white/90 px-4 py-2.5 text-sm text-gray-900 shadow-sm focus:border-indigo-400 focus:ring-indigo-400 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
              >
                <option value="ACTIVITY">
                  {t("classroomDetails.calendarModal.types.activity", {
                    defaultValue: "Activity",
                  })}
                </option>
                <option value="OUTING">
                  {t("classroomDetails.calendarModal.types.outing", {
                    defaultValue: "Outing",
                  })}
                </option>
                <option value="OTHER">
                  {t("classroomDetails.calendarModal.types.other", {
                    defaultValue: "Other",
                  })}
                </option>
              </select>
            </label>

            <label className="mt-7 flex items-start gap-3 rounded-2xl border border-emerald-100 bg-emerald-50/60 p-3 text-sm font-semibold text-gray-800 dark:border-emerald-800/50 dark:bg-emerald-950/30 dark:text-gray-100 sm:mt-8">
              <input
                type="checkbox"
                checked={calendarForm.visibleInParentPortal}
                onChange={(event) =>
                  setCalendarForm((current) => ({
                    ...current,
                    visibleInParentPortal: event.target.checked,
                  }))
                }
                className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              />
              <span>
                {t("classroomDetails.calendarModal.visibleInParentPortal", {
                  defaultValue: "Show in parent portal",
                })}
              </span>
            </label>
          </div>

          <label className="flex items-center gap-3 rounded-2xl border border-gray-200/70 bg-white/80 p-3 text-sm font-semibold text-gray-800 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-100">
            <input
              type="checkbox"
              checked={calendarForm.allDay}
              onChange={(event) =>
                setCalendarForm((current) => ({ ...current, allDay: event.target.checked }))
              }
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            {t("classroomDetails.calendarModal.allDay", { defaultValue: "All day" })}
          </label>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm font-semibold text-gray-800 dark:text-gray-100">
              {t("classroomDetails.calendarModal.startsAt", { defaultValue: "Starts at" })}
              <input
                type="date"
                value={calendarForm.startDate}
                onChange={(event) =>
                  setCalendarForm((current) => ({
                    ...current,
                    startDate: event.target.value,
                    endDate: current.endDate || event.target.value,
                  }))
                }
                className="mt-2 w-full rounded-2xl border border-gray-200/80 bg-white/90 px-4 py-2.5 text-sm text-gray-900 shadow-sm focus:border-indigo-400 focus:ring-indigo-400 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
              />
              {!calendarForm.allDay && (
                <input
                  type="time"
                  value={calendarForm.startTime}
                  onChange={(event) =>
                    setCalendarForm((current) => ({ ...current, startTime: event.target.value }))
                  }
                  className="mt-2 w-full rounded-2xl border border-gray-200/80 bg-white/90 px-4 py-2.5 text-sm text-gray-900 shadow-sm focus:border-indigo-400 focus:ring-indigo-400 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
                />
              )}
            </label>

            <label className="block text-sm font-semibold text-gray-800 dark:text-gray-100">
              {t("classroomDetails.calendarModal.endsAt", { defaultValue: "Ends at" })}
              <input
                type="date"
                value={calendarForm.endDate}
                onChange={(event) =>
                  setCalendarForm((current) => ({ ...current, endDate: event.target.value }))
                }
                className="mt-2 w-full rounded-2xl border border-gray-200/80 bg-white/90 px-4 py-2.5 text-sm text-gray-900 shadow-sm focus:border-indigo-400 focus:ring-indigo-400 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
              />
              {!calendarForm.allDay && (
                <input
                  type="time"
                  value={calendarForm.endTime}
                  onChange={(event) =>
                    setCalendarForm((current) => ({ ...current, endTime: event.target.value }))
                  }
                  className="mt-2 w-full rounded-2xl border border-gray-200/80 bg-white/90 px-4 py-2.5 text-sm text-gray-900 shadow-sm focus:border-indigo-400 focus:ring-indigo-400 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
                />
              )}
            </label>
          </div>

          {calendarError && (
            <p className="rounded-2xl border border-amber-200/70 bg-amber-50/80 px-4 py-3 text-sm text-amber-800 dark:border-amber-800/50 dark:bg-amber-900/30 dark:text-amber-100">
              {calendarError}
            </p>
          )}
        </div>
      </Modal>

      {canReadOutings && <OutingModals outings={outings} t={t} canManage={canOutingManage} />}
    </LdsPage>
  );
};

export default ClassroomDetails;
